import { useEffect, lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { TopBar } from './TopBar';
import { BottomNav } from './BottomNav';
import { TrialBanner } from './TrialBanner';
import { DirectedSequenceGate } from './DirectedSequenceGate';
import { CommandPalette } from '../common/CommandPalette';
import { InstallPrompt } from '../ui/InstallPrompt';
import { OfflineBanner } from '../ui/OfflineBanner';
import { BadgeEarnedModal } from '../gamification/BadgeEarnedModal';
import { MilestoneModal } from '../gamification/MilestoneModal';
import { ThemeParticles } from '../effects/ThemeParticles';
import { useChildStore } from '../../store';
import { useSettingsStore } from '../../store/settingsStore';
import { useUIStore } from '../../store/uiStore';
import { useActiveShopTheme } from '../../hooks/useActiveShopTheme';
import { isBetaFeatureEnabled } from '../../config/beta-surface';
import { getAppSkin } from '../../data/parent-skin-presets';

// Lazy so the PIN-lock chunk only loads when actually needed (parents who
// have already set a PIN never download it via this gate).
const ParentPinLock = lazy(() =>
  import('../auth/ParentPinLock').then((m) => ({ default: m.ParentPinLock })),
);

// Singleton WeeklyWrapUp ceremony — bound to the AppShell so the celebration
// modal only ever exists once in the tree. Previously this was mounted on
// both ParentPage and the kid CompetitionsPage, which risked double-rendering
// the modal off the same `pendingCeremony` flag.
const WeeklyWrapUp = lazy(() =>
  import('../competitions/WeeklyWrapUp').then((m) => ({ default: m.WeeklyWrapUp })),
);

const CSS_VAR_MAP = [
  ['primary', '--color-theme-primary'],
  ['primaryLight', '--color-theme-primary-light'],
  ['secondary', '--color-theme-secondary'],
  ['secondaryLight', '--color-theme-secondary-light'],
  ['gradientFrom', '--color-theme-gradient-from'],
  ['gradientTo', '--color-theme-gradient-to'],
  ['topBarBg', '--color-theme-topbar-bg'],
  ['topBarBorder', '--color-theme-topbar-border'],
  ['navBg', '--color-theme-nav-bg'],
  ['xpBarFrom', '--color-theme-xp-from'],
  ['xpBarTo', '--color-theme-xp-to'],
  ['progressRing', '--color-theme-progress-ring'],
  ['levelUpFrom', '--color-theme-levelup-from'],
  ['levelUpTo', '--color-theme-levelup-to'],
  ['cardAccent', '--color-theme-card-accent'],
  ['cardAccentBg', '--color-theme-card-accent-bg'],
  ['textPrimary', '--color-theme-text-primary'],
  ['textSecondary', '--color-theme-text-secondary'],
  ['surfaceBg', '--color-theme-surface-bg'],
  ['surfaceBorder', '--color-theme-surface-border'],
] as const;

export function AppShell() {
  const { children } = useChildStore();
  const parentPin = useSettingsStore((s) => s.parentPin);
  const appSkinId = useSettingsStore((s) => s.appSkin);
  const immersive = useUIStore((s) => s.immersive);
  const shopTheme = useActiveShopTheme();
  const showCompetitions = isBetaFeatureEnabled('competitions');

  // Apply theme CSS variables to <html>.
  // Precedence: shop theme (if equipped + reward economy on) > parent-picked
  // app skin > the @theme defaults in index.css.
  useEffect(() => {
    const root = document.documentElement;

    const colors = shopTheme
      ? shopTheme.colors
      : getAppSkin(appSkinId).colors;

    for (const [key, cssVar] of CSS_VAR_MAP) {
      const value = colors[key as keyof typeof colors];
      if (value) root.style.setProperty(cssVar, value);
    }

    if (shopTheme) {
      root.dataset.shopTheme = shopTheme.id;
      delete root.dataset.appSkin;
    } else {
      root.dataset.appSkin = appSkinId;
      delete root.dataset.shopTheme;
    }

    return () => {
      for (const [, cssVar] of CSS_VAR_MAP) {
        root.style.removeProperty(cssVar);
      }
      delete root.dataset.shopTheme;
      delete root.dataset.appSkin;
    };
  }, [shopTheme, appSkinId]);

  if (children.length === 0) {
    return <Outlet />;
  }

  // ─── Mandatory parent PIN gate ───────────────────────────────────────────
  // Once an account has at least one child profile, the parent dashboard,
  // settings, soft-deleted-trash, billing info, and child-account data are
  // all reachable from inside the app. None of those should be accessible
  // without a PIN. Render the lock as a blocking overlay BEFORE the app
  // chrome (TopBar/Outlet/BottomNav) ever mounts, so a child can't navigate
  // around the gate via keyboard shortcuts or URL bar manipulation.
  //
  // `allowSkip={false}` (default) hides the "Skip for now" button — the
  // parent must complete a 4-digit PIN + confirmation to dismiss this gate.
  // `allowCancel={false}` hides the X close button + suppresses Esc, since
  // there's nowhere safe to "cancel" to from a freshly-onboarded account.
  // Once `setParentPin(hashed)` writes to settingsStore, this branch falls
  // through automatically and the normal app shell renders.
  if (!parentPin) {
    return (
      <Suspense fallback={null}>
        <ParentPinLock
          onUnlock={() => { /* no-op — selector reactivity dismisses overlay once parentPin is set */ }}
          onCancel={() => { /* no cancel target — gate is mandatory */ }}
          allowCancel={false}
        />
      </Suspense>
    );
  }

  return (
    // `immersive-mode` (set by useUIStore — see GamesPage) zeroes
    // --bottom-nav-h via a CSS class in index.css so every descendant
    // computing var(--safe-game-h) reclaims the BottomNav's 56px. We also
    // hide BottomNav itself and strip <main>'s pb-20 padding so the game
    // owns that full slice. TopBar and the (conditional) banners stay
    // visible because they carry useful in-game state and TopBar isn't
    // what was clipping the keyboard. The game's own Exit button is the
    // navigate-out affordance — see GamesPage's onExit handlers.
    <div className={`min-h-dvh flex flex-col relative ${immersive ? 'immersive-mode' : ''}`}>
      {/* Skip link — visible only when focused via keyboard. Lets screen-reader
          users jump past the top bar straight to page content. */}
      <a
        href="#main-content"
        className="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-[100] focus-visible:px-3 focus-visible:py-2 focus-visible:rounded-lg focus-visible:bg-indigo-600 focus-visible:text-white focus-visible:font-bold focus-visible:shadow-lg"
      >
        Skip to main content
      </a>
      {shopTheme && <ThemeParticles config={shopTheme.particles} themeId={shopTheme.id} />}
      <OfflineBanner />
      <TrialBanner />
      <TopBar />
      <main id="main-content" className={`flex-1 ${immersive ? '' : 'pb-20'} overflow-y-auto relative z-[3]`}>
        <DirectedSequenceGate />
      </main>
      <InstallPrompt />
      {!immersive && <BottomNav />}
      <CommandPalette />
      {/* Singleton sibling-competition ceremony — only renders content when
          `pendingCeremony` flips true after a week rollover. Self-gated by
          its own internal check; mounting it here guarantees one instance. */}
      {showCompetitions && children.length >= 2 && (
        <Suspense fallback={null}>
          <WeeklyWrapUp />
        </Suspense>
      )}
      {/* App-wide celebrations — driven by the celebration queue so any path
          (dashboard, activity, course, quiz, quest) celebrates here. */}
      <BadgeEarnedModal />
      <MilestoneModal />
    </div>
  );
}
